import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetUsers, useDeleteUser, useUpdateUser } from "../../api/hooks/useUser";
import { showSuccess, showError } from "../../helpers/toast";
import type { User } from "../../api/types/user.types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Pagination from "../../components/Panigation";

interface UserListProps {
  searchTerm: string;
  isBlockedFilter?: boolean | undefined;
}

const UserList: React.FC<UserListProps> = ({ searchTerm, isBlockedFilter }) => {
  const navigate = useNavigate();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch users
  const { data: usersData, isLoading: isLoadingUsers, refetch: refetchUsers, error: usersError } = useGetUsers(
    {
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
      ...(isBlockedFilter !== undefined && { isBlocked: isBlockedFilter }),
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { enabled: true }
  );

  // Extract users and totalCount from response
  const { users, totalCount } = useMemo(() => {
    const data = usersData?.data;
    let usersList: User[] = [];
    let total = 0;

    if (Array.isArray(data)) {
      usersList = data;
      total = data.length;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        usersList = dataObj.list;
        total = dataObj.totalCount || dataObj.list.length;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        usersList = dataObj.data;
        total = dataObj.totalCount || dataObj.data.length;
      } else if (dataObj.totalCount !== undefined) {
        total = dataObj.totalCount;
      }
    }

    if (!Array.isArray(usersList)) {
      usersList = [];
    }

    return {
      users: usersList,
      totalCount: total || usersList.length,
    };
  }, [usersData]);

  // Delete user mutation
  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser({
    onSuccess: (response) => {
      showSuccess(response.message || "User deleted successfully");
      refetchUsers();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete user");
    },
  });

  // Update user status mutation
  const { mutate: updateUserStatus, isPending: isUpdatingStatus } = useUpdateUser({
    onSuccess: (response) => {
      showSuccess(response.message || "User status updated successfully");
      refetchUsers();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update user status");
    },
  });

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  // Handle status toggle
  const handleStatusToggle = (user: User) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateUserStatus({
      id: user?._id || '',
      data: { isBlocked: event.target.checked },
    });
  };

  // Handle delete user
  const handleDeleteUser = (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete user
  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser(userToDelete);
    }
  };

  // Cancel delete user
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Handle view user
  const handleViewUser = (user: User) => {
    navigate("/manage-users/detail", { state: { id: user?._id || '' } });
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    navigate("/manage-users/edit", { state: { userId: user?._id || '' } });
  };

  const totalPages = Math.ceil(totalCount / limit);
  const onPageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <TableContainer className="table_container">
        {isLoadingUsers ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : usersError ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <p>Error loading users. Please try again.</p>
          </Box>
        ) : users.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <p>No users found.</p>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User Detail</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Block/Unblock</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {users.map((user: User, index: number) => (
                <TableRow hover key={user?._id || ''}>
                  {/* USER DETAILS */}
                  <TableCell className="click_action">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewUser(user);
                      }}
                      className="user_block click_action"
                      style={{ cursor: 'pointer' }}
                    >
                      <figure>
                        <Avatar
                          src={user?.image || '' || "/static/images/user_placeholder.png"}
                          alt={user?.fullName || ''}
                          sx={{ width: 40, height: 40 }}
                        />
                      </figure>
                      <p>
                        <b>{user?.fullName || ''}</b>
                      </p>
                    </a>
                  </TableCell>

                  {/* EMAIL */}
                  <TableCell>{user?.email || ''}</TableCell>

                  {/* PHONE */}
                  <TableCell>
                    {user?.countryCode || ''} {user?.phone || ''}
                  </TableCell>

                  {/* STATUS */}
                  <TableCell>
                    <span className={user?.isBlocked || '' ? "status_blocked" : "status_active"}>
                      {user?.isBlocked || '' ? "Blocked" : "Active"}
                    </span>
                  </TableCell>

                  {/* CREATED DATE */}
                  <TableCell>{formatDate(user?.createdAt || '')}</TableCell>

                  {/* BLOCK/UNBLOCK TOGGLE */}
                  <TableCell>
                    <SwitchToggle
                      checked={!user?.isBlocked || false} // Inverted: checked = active, unchecked = blocked
                      onChange={(e) => {
                        const newEvent = {
                          ...e,
                          target: {
                            ...e.target,
                            checked: !e.target.checked, // Invert the checked value
                          },
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleStatusToggle(user)(newEvent);
                      }}
                      disabled={isLoadingUsers || isDeleting || isUpdatingStatus}
                    />
                  </TableCell>

                  {/* ACTION BUTTONS */}
                  <TableCell>
                    <div className="table_actions v2">
                      <IconButton
                        onClick={() => handleViewUser(user)}
                        disabled={isLoadingUsers || isDeleting || isUpdatingStatus}
                        title="View User"
                      >
                        <img src="/static/images/eye_icon.svg" alt="View" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEditUser(user)}
                        disabled={isLoadingUsers || isDeleting || isUpdatingStatus}
                        title="Edit User"
                      >
                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteUser(user?._id || '')}
                        disabled={isLoadingUsers || isDeleting || isUpdatingStatus}
                        title="Delete User"
                      >
                        <img src="/static/images/trash2_icon.svg" alt="Delete" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!isLoadingUsers && !usersError && totalCount > 0 && (
        <Pagination
          module={users}
          page={page}
          onPageChange={onPageChange}
          totalPages={totalPages}
          totalCount={totalCount}
          setPage={setPage}
          limit={limit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default UserList;
